import { computed, makeObservable, observable } from 'mobx';
import { Ajax2 } from '@shared/Ajax';
import { ProjectCodesService } from '@shared/services/ProjectCodesService';
import { Notify } from '@shared/ui-native/Notify';
import { FileTreeService } from '@shared/services/FileTreeService';
import { CrossSectionUtils } from '@pages/cross-section/shared/CrossSectionUtils';
import { CrossSectionCanvas } from '@pages/cross-section/shared/CrossSectionCanvas';
import { LongSectionDrawableModel } from '@pages/cross-section/long-section/LongSectionDrawableModel';
import { LongSectionLogpoint } from '@pages/cross-section/long-section/LongSectionLogpoint';
import Konva from 'konva';


interface ILongSectionResponse {
    stationStart: number;
    stationEnd: number;
    length: number;
    alignment: ILongSectionDrawableObject;
    terrainModels: any[];
    backgroundModels: any[];
    models: ILongSectionDrawableObject[];
    pipes: ILongSectionDrawableObject[];
    logpoints: any[];
}

// data set on init to define what is loaded on scene
export interface ILongSectionLoadSelection {
    alignmentId: number;
    alignmentIndex: number;
    includeModelIds?: number[];
    selectedLogpointId?: number;
}


export class LongSectionStore {
    @observable.ref csCanvas: CrossSectionCanvas = null;
    @observable.ref loadSelection: ILongSectionLoadSelection = null;
    @observable.ref latestServerData: ILongSectionResponse = null;

    @observable isLoading = false;
    @observable isLoadingTree = true;
    @observable isCreatingPdf = false;

    @observable hiddenPipeIds: number[] = [];
    @observable hiddenLogpointIds: number[] = [];
    @observable hiddenModelIds: Array<string | number> = [];

    modelNamesMap: Record<number, string>;

    @observable settings = {
        stationStart: null,
        stationEnd: null,
        searchDisplacement: 2.5
    };

    @computed
    get getModelsControls() {
        if (!this.latestServerData) {
            return [];
        }
        console.log('CALC getModelsControls:', this.latestServerData.models);
        return CrossSectionUtils.getModelsControls(this.latestServerData.models, this.latestServerData.logpoints, this.latestServerData.pipes, this.modelNamesMap);
    }

    constructor(private treeData?: ITreeData, private view?: CrossSectionViewState) {
        makeObservable(this);
        this.fetchInitialData();
    }

    destroy() {
        if (this.csCanvas) {
            this.csCanvas.destroy();
        }
    }

    setCanvasEl = async (canvasWrapperEl: HTMLDivElement, loadSelection: ILongSectionLoadSelection) => {
        if (this.csCanvas) {
            return;
        }
        try {
            console.log('setCanvasEl:', canvasWrapperEl);
            this.csCanvas = new CrossSectionCanvas({
                zoomToCursorCorrection: this.zoomToCursorCorrection,
                view: this.view
            });
            await this.csCanvas.init({
                canvasWrapperEl,
                onViewChanged: this.drawAll
            });
            await this.fetchDrawing(loadSelection);
        } catch (e) {
            console.log('e:', e);
            Notify.error('Failed to initialize cross-section scene');
        }
    }

    fetchInitialData = () => {
        this.isLoadingTree = true;
        ProjectCodesService.init();

        let treeDataPromise = this.treeData ? Promise.resolve(this.treeData) : FileTreeService.fetchFileTree();
        treeDataPromise.then(td => {
            const namesMap = {}
            FileTreeService.eachPostOrder(td, 0, node => {
                if (node.models) {
                    node.models.forEach(m => {
                        namesMap[m.id] = m.name;
                    })
                }
            })
            this.modelNamesMap = namesMap;
        }).catch(Notify.error).finally(() => this.isLoadingTree = false)
    }

    fetchDrawing = async (loadSelection?: ILongSectionLoadSelection) => {
        this.loadSelection = loadSelection ?? this.loadSelection;
        this.isLoading = true;

        Ajax2({
            url: 'long_section.json',
            data: {
                projectId: kuura.getProjectId(),
                logpointDelta: this.settings.searchDisplacement,
                includeModelIds: this.loadSelection.includeModelIds?.join(','),
                alignmentId: this.loadSelection.alignmentId,
                alignmentIndex: this.loadSelection.alignmentIndex,
                startStation: this.settings.stationStart,
                endStation: this.settings.stationEnd
            }
        }).then((resp: { longSection: ILongSectionResponse }) => {
            this.preselectModels(resp.longSection);
            this.drawAll();
        }).catch(e => {
            //showErrors(data);
            Notify.error('Failed to load Cross Section');
            console.log('e:', e);
        }).finally(() => {
            this.isLoading = false;
        })
    }

    preselectModels = (serverData: ILongSectionResponse) => {
        serverData.models.unshift(serverData.alignment);
        serverData.alignment.name = this.modelNamesMap[serverData.alignment.modelId];

        this.settings.stationStart = parseFloat(serverData.stationStart.toFixed(2));
        this.settings.stationEnd = parseFloat(serverData.stationEnd.toFixed(2));
        if (this.csCanvas.widthMeters === 0) {
            this.csCanvas.widthMeters = this.csCanvas.widthMetersInitial = serverData.length + 10;

            // call order matters here
            this.updateLowestPointAndYScale(serverData);
            this.csCanvas.clearStage();
            this.panYInitialPosition();
        }

        this.csCanvas.initialPanY = this.csCanvas.panY;
        this.latestServerData = serverData;
    }


    drawAll = () => {
        const data: ILongSectionResponse = this.latestServerData;
        if (!data) {
            console.log('drawAll - no data, returning');
            return;
        }

        this.csCanvas.clearStage();
        this.drawGridLongSection();

        data.models.forEach(model => {
            this.drawModel(model, false, 'model');
        })
        data.terrainModels.forEach(model => {
            this.drawModel(model, false, 'terrainModel');
        })

        data.logpoints.forEach(logpoint => {
            if (!this.hiddenLogpointIds.includes(logpoint.modelId)) {
                LongSectionLogpoint.draw({
                    logpoint,
                    stationStart: this.settings.stationStart,
                    highlighted: logpoint.id === this.loadSelection?.selectedLogpointId,
                    csCanvas: this.csCanvas,
                    onClickCallback: () => {}
                })
            }
        })

        this.csCanvas.drawHeader(data.alignment.name, this.settings.stationStart ? `[${this.settings.stationStart} - ${this.settings.stationEnd}]` : null);
    }

    drawModel = (model: ILongSectionDrawableObject, isBackground: boolean, type: 'model' | 'terrainModel') => {
        if (this.hiddenModelIds.includes(model.modelId)) {
            return;
        }

        model.name = model.name ?? this.modelNamesMap[model.modelId];
        LongSectionDrawableModel.draw({
            modelLine: model,
            type,
            isBackground,
            csCanvas: this.csCanvas,
            onClickCallback: (alignment, x, y) => { }
        });
    }

    drawGridLongSection() {
        this.csCanvas.staticLayer.findOne('#grid')?.destroy();
        const group = new Konva.Group({
            id: 'grid'
        });

        const canvasOptions = this.csCanvas.canvasOptions;
        const panX = this.csCanvas.panX;
        const panY = this.csCanvas.panY;
        const gridlinesOffset = 50;

        const halfWidth = (this.csCanvas.width - canvasOptions.side_margin) / 2;
        var center = this.csCanvas.getCenter();
        var x1 = center - halfWidth;
        var x2 = center + halfWidth;
        var lowestPoint = this.csCanvas.lowestPoint;
        var lineStroke = 'gray';
        var textColor = 'black';

        var xStep = Math.round(this.latestServerData.length / 300.0) * 10;
        xStep = xStep * this.csCanvas.widthMeters / this.csCanvas.widthMetersInitial; // adjust x-axel numbers/grid lines according to zoom level
        xStep = Math.round(xStep/5)*5;
        if (xStep < 5) {
            xStep = 5;
        }
        var fontSize = canvasOptions.x_scale * xStep / 3.5;
        fontSize = Math.min(20, fontSize);
        if (this.latestServerData.length > 9999) {
            fontSize = Math.max(10, fontSize);
        } else {
            fontSize = Math.max(15, fontSize);
        }

        //horizontal lines
        let previousY = 1000;
        for (let y = this.csCanvas.height - canvasOptions.bottom_margin + canvasOptions.y_scale * gridlinesOffset, z = lowestPoint - gridlinesOffset;
             y >= canvasOptions.top_margin - canvasOptions.y_scale * gridlinesOffset; y -= canvasOptions.y_scale, z++) {
            if (y + panY > this.csCanvas.height - canvasOptions.bottom_margin || y + panY < canvasOptions.top_margin) {
                continue;
            }
            if (previousY - (y + panY) < 15) {
                continue;
            }
            if (Math.abs(y + panY - this.csCanvas.height / 2) < Math.abs(previousY - this.csCanvas.height / 2)) {
                this.csCanvas.yCenterLine = z;
            }
            previousY = y + panY;

            const line = new Konva.Line({
                points: [x1 - panX - 10, y + panY, x2 - panX, y + panY],
                stroke: lineStroke,
                strokeWidth: 1,
                lineCap: 'round',
                lineJoin: 'round',
                listening: false
            });
            if (canvasOptions.y_scale >= 20 && (y - 4 * (canvasOptions.y_scale / 5.0) + panY) > canvasOptions.top_margin) {
                for (var i = 1; i < 5; i++) {
                    var y2 = y - i * (canvasOptions.y_scale / 5.0);

                    var subline = new Konva.Line({
                        points: [x1 - 10.0 - panX, y2 + panY, x1 - panX, y2 + panY],
                        stroke: lineStroke,
                        strokeWidth: 1,
                        lineCap: 'round',
                        lineJoin: 'round',
                        listening: false
                    });
                    group.add(subline);
                }
            }

            let numText: any = Math.floor(z);

            const leftText = new Konva.Text({
                x: x1 - 15 - panX,
                y: y + panY,
                text: numText,
                fontSize: Math.min(18, fontSize),
                fontFamily: 'Calibri',
                fill: textColor,
                listening: false
            });
            const rightText = new Konva.Text({
                x: x2 + 5 - panX,
                y: y + panY,
                text: numText,
                fontSize: Math.min(18, fontSize),
                fontFamily: 'Calibri',
                fill: textColor,
                listening: false
            });
            leftText.offsetX(leftText.width());
            leftText.offsetY(leftText.height() / 2);
            rightText.offsetY(rightText.height() / 2);

            group.add(line);
            group.add(leftText);
            group.add(rightText);
        }

        // Vertical lines
        var previousX;
        for (var i = this.settings.stationStart - gridlinesOffset * xStep, x = canvasOptions.side_margin / 2 - (canvasOptions.x_scale * xStep * gridlinesOffset);
             i <= this.settings.stationEnd + gridlinesOffset * xStep; i += xStep, x += (canvasOptions.x_scale * xStep)) {

            var xCoord = x + panX;
            if (xCoord < canvasOptions.side_margin / 2) {
                continue;
            }
            if (xCoord > this.csCanvas.width - (canvasOptions.side_margin / 2 + 1)) {
                continue;
            }
            if (Math.abs(xCoord - this.csCanvas.width / 2) < Math.abs(previousX - this.csCanvas.width / 2)) {
                this.csCanvas.xCenterLine = i;
            }
            previousX = xCoord;

            const line = new Konva.Line({
                points: [xCoord, this.csCanvas.height - canvasOptions.bottom_margin, xCoord, canvasOptions.top_margin],
                stroke: lineStroke,
                strokeWidth: 1,
                lineCap: 'round',
                lineJoin: 'round',
                listening: false
            });
            group.add(line);

            let numText: any = parseInt(i + '');
            if (i !== numText) {
                numText = i.toFixed(1);
            }
            const text = new Konva.Text({
                x: xCoord,
                y: this.csCanvas.height - canvasOptions.bottom_margin + 10,
                text: numText,
                fontSize: fontSize,
                fontFamily: 'Calibri',
                fill: textColor,
                listening: false
            });
            text.offsetX(text.width() / 2);
            group.add(text);
        }
        // left and right border lines
        var line = new Konva.Line({
            points: [this.csCanvas.width - (canvasOptions.side_margin / 2), this.csCanvas.height - canvasOptions.bottom_margin,
                this.csCanvas.width - (canvasOptions.side_margin / 2), canvasOptions.top_margin],
            stroke: lineStroke,
            strokeWidth: 2,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false
        });
        group.add(line);
        line = new Konva.Line({
            points: [canvasOptions.side_margin / 2, this.csCanvas.height - canvasOptions.bottom_margin, canvasOptions.side_margin / 2, canvasOptions.top_margin],
            stroke: lineStroke,
            strokeWidth: 2,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false
        });
        group.add(line);
        this.csCanvas.staticLayer.add(group);
    }


    updateLowestPointAndYScale = (longSection: ILongSectionResponse) => {
        const SetYScale = () => {
            let deltaY = this.csCanvas.highestPoint - this.csCanvas.lowestPoint;
            if (deltaY < 1) {
                deltaY = 1;
            }
            this.csCanvas.yScale = Math.round(this.csCanvas.widthMeters / (8 * deltaY));
            if (this.csCanvas.yScale < 1.0) {
                this.csCanvas.yScale = 1.0;
            }
            this.csCanvas.yScaleInitial = this.csCanvas.yScale;
        }

        this.csCanvas.highestPoint = -1000;
        this.csCanvas.lowestPoint = 10000;

        // longSection.models will include longSection.alignment
        longSection.models.forEach(modelline => {
            modelline.vectors.forEach(vector => {
                for (var j = 0; j < vector.points.length; j++) {
                    if (vector.points[j].y < this.csCanvas.lowestPoint && vector.points[j].y > -1000) {
                        this.csCanvas.lowestPoint = vector.points[j].y;
                    }
                    if (vector.points[j].y > this.csCanvas.highestPoint) {
                        this.csCanvas.highestPoint = vector.points[j].y;
                    }
                }
            });
        });
        if (this.csCanvas.highestPoint > -1000) {
            SetYScale();
            return;
        }
        this.csCanvas.lowestPoint = 0;
        this.csCanvas.highestPoint = 30;
        SetYScale();
    }

    panYInitialPosition = () => {
        const lowestPoint = this.csCanvas.lowestPoint;
        const maxAlignmentY = this.csCanvas.height / 4;
        this.csCanvas.panY = maxAlignmentY - ((this.csCanvas.height - this.csCanvas.canvasOptions.bottom_margin) +
            lowestPoint * this.csCanvas.canvasOptions.y_scale -
            this.csCanvas.highestPoint * this.csCanvas.canvasOptions.y_scale);

            console.log('panYInitialPosition new', this.csCanvas.canvasOptions.y_scale)
    }

    createPdf = async () => {
        this.isCreatingPdf = true;
        const filename = this.latestServerData.alignment.name + ".pdf";
        await CrossSectionUtils.createPdf(filename, this.csCanvas.stage, this.getModelsControls);
        this.isCreatingPdf = false;
    }

    crossSectionToolStation = 0;
    @observable crossSectionToolActive = false;

    toggleCrossSectionTool = () => {
        this.crossSectionToolActive = !this.crossSectionToolActive;

        if (!this.crossSectionToolActive) {
            this.csCanvas.clearTooltips();
            this.csCanvas.canvasWrapperEl.removeEventListener('click', this.crossSectionToolClickHandler);
            this.csCanvas.canvasWrapperEl.removeEventListener('mousemove', this.drawCrossSectionTool);
            this.drawAll();
        } else {
            this.csCanvas.canvasWrapperEl.addEventListener('click', this.crossSectionToolClickHandler);
            this.csCanvas.canvasWrapperEl.addEventListener('mousemove', this.drawCrossSectionTool);

        }
    }

    crossSectionToolClickHandler = e => {
        if (e.which === 1) {
            this.toggleCrossSectionTool();

            var url = [];
            url.push(kuura.getContextPath());
            url.push("/cross_section.htm");
            url.push("?alignmentId=");
            url.push(this.loadSelection.alignmentId);
            url.push("&station=");
            url.push(this.crossSectionToolStation.toFixed(2));

            if (this.loadSelection.includeModelIds) {
                url.push("&includeModelIds=");
                url.push(this.loadSelection.includeModelIds);
            }
            window.open(url.join(''));
        }
    }

    drawCrossSectionTool = () => {
        if (!this.crossSectionToolActive) {
            return;
        }
        this.csCanvas.clearTooltips();
        const canvasOptions = this.csCanvas.canvasOptions;
        const mouseX = this.csCanvas.mouseX;
        const mouseY = this.csCanvas.mouseY;
        const startStation = this.latestServerData.stationStart !== null ? this.latestServerData.stationStart : 0;

        const line = new Konva.Line({
            visible: true,
            points: [mouseX, this.csCanvas.height - canvasOptions.bottom_margin, mouseX, canvasOptions.top_margin],
            stroke: getRgb("255,0,0"),
            strokeWidth: 2,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false
        });
        line.dash([3, 4]);

        this.crossSectionToolStation = startStation - this.csCanvas.panX / canvasOptions.x_scale + Math.abs(mouseX / canvasOptions.x_scale) - canvasOptions.side_margin / (canvasOptions.x_scale*2);
        const xOffset = 25;
        const textLine = "Station: " + this.crossSectionToolStation.toFixed(2) + " m";
        const text = new Konva.Text({
            x: mouseX + xOffset,
            y: mouseY,
            text: textLine,
            fontSize: 14,
            fontFamily: 'Calibri',
            fill: 'red',
            listening: false
        });

        this.csCanvas.tooltipLayer.add(line);
        this.csCanvas.tooltipLayer.add(text);
        this.csCanvas.tooltipLayer.draw();
    }

    zoomToCursorCorrection = () => {
        const canvasOptions = this.csCanvas.canvasOptions;
        const lowestPoint = this.csCanvas.lowestPoint;
        const mouseX = this.csCanvas.mouseX;
        const mouseY = this.csCanvas.mouseY;

        var center = canvasOptions.side_margin / 2 + this.csCanvas.panX;
        var pointX = (mouseX - center) / this.csCanvas.oldXScale;
        this.csCanvas.panX = mouseX - (canvasOptions.side_margin / 2) - (pointX * canvasOptions.x_scale);

        var pointY = ((this.csCanvas.height - canvasOptions.bottom_margin) + lowestPoint * this.csCanvas.oldYScale + this.csCanvas.panY - mouseY) / this.csCanvas.oldYScale;
        this.csCanvas.panY = mouseY + (pointY * canvasOptions.y_scale) - lowestPoint * canvasOptions.y_scale - (this.csCanvas.height - canvasOptions.bottom_margin);
    }

    clearAllTools = () => {

    }

    getState = () => {
        if (!this.csCanvas) {
            return null;
        }

        return {
            loadSelection: this.loadSelection,
            searchDisplacement: this.settings.searchDisplacement,
            stationStart: this.settings.stationStart,
            stationEnd: this.settings.stationEnd,
            view: this.csCanvas.getViewState()
        }
    }
}

const getRgb = (color: string) => 'rgb(' + color + ')'
